import React, { useEffect, useState } from 'react';
import { Heart, Compass, Mountain, Users } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import '../styles/About.css';

const About = () => {
  const { t } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
    window.scrollTo(0, 0);
  }, []);

  const values = [
    { icon: <Mountain size={32} />, title: 'Small Groups', description: 'Intimate experiences that allow for meaningful connections with nature and culture.' },
    { icon: <Compass size={32} />, title: 'Thoughtful Routes', description: 'Carefully designed paths that reveal Madeira\'s authentic character and hidden stories.' },
    { icon: <Heart size={32} />, title: 'Respect for the Island', description: 'Every experience honors the land, the people, and the rhythms of life here.' },
    { icon: <Users size={32} />, title: 'Human Connection', description: 'Real moments shared between curious travelers, local guides, and the people of Madeira.' }
  ];

  // Real Elvio photos — founder & guide. Portraits, must remain fully visible.
  const elvioPhotos = [
    { src: 'https://customer-assets.emergentagent.com/job_15838cb8-d5c8-4088-b574-f4de08159ff9/artifacts/uwwseim5_IMG_6715.jpg', alt: 'Elvio at Pico Ruivo summit at sunrise', caption: 'Sunrise on Pico Ruivo — 1,862 m above the clouds.' },
    { src: 'https://customer-assets.emergentagent.com/job_15838cb8-d5c8-4088-b574-f4de08159ff9/artifacts/m34w48hh_IMG_6716.jpg', alt: 'Elvio embracing an ancient laurel tree', caption: 'Inside the Laurissilva — older than us, calmer than us.' },
    { src: 'https://customer-assets.emergentagent.com/job_15838cb8-d5c8-4088-b574-f4de08159ff9/artifacts/zfvyu54d_IMG_6717.jpg', alt: 'Elvio at sunset on a coastal trail, wearing the Madeira Outdoors t-shirt', caption: 'Ponta de São Lourenço at golden hour.' },
    { src: 'https://customer-assets.emergentagent.com/job_15838cb8-d5c8-4088-b574-f4de08159ff9/artifacts/4vzaptpg_IMG_6718.jpg', alt: 'Elvio with a friendly Madeira goat', caption: 'Conversations along the trail — even with the locals.' }
  ];

  return (
    <div className="about-page" data-testid="about-page">
      {/* Page Header */}
      <section className="page-hero-about">
        <div className="page-hero-overlay"></div>
        <div className="container">
          <div className={`page-hero-content ${isVisible ? 'animated fadeIn' : 'animated'}`}>
            <h1 className="display-large" data-testid="about-hero-title">{t('about.hero')}</h1>
            <p className="about-hero-subtitle">{t('about.heroSub')}</p>
          </div>
        </div>
      </section>

      {/* Meet Elvio — Founder photo gallery (real photos, fully visible) */}
      <section className="about-photo-wall-section" data-testid="about-photo-wall">
        <div className="container">
          <div className="about-photo-wall-intro">
            <span className="story-block-label">Meet Elvio</span>
            <h2 className="heading-2">The Founder & Guide</h2>
            <p className="body-large">
              Four frames. Four stories. The man behind Madeira Outdoors, out where he is most at home.
            </p>
          </div>
          <div className="elvio-gallery">
            {elvioPhotos.map((photo, i) => (
              <figure key={i} className="elvio-tile">
                <div className="elvio-tile-img">
                  <img src={photo.src} alt={photo.alt} loading="lazy" />
                </div>
                <figcaption>{photo.caption}</figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* Meet the Humans — Origin Story */}
      <section className="story-section-narrative" data-testid="about-origin-section">
        <div className="container">
          <div className="story-grid">
            <div className="story-main">
              <span className="story-block-label">Our Story</span>
              <h2 className="story-title">Meet the Humans Behind Madeira Outdoors</h2>

              <div className="story-paragraphs">
                <p>What does it really take to reconnect with a place you thought you already knew?</p>
                <p>How does one turn a simple return home into a life project built around adventure, culture and human connection?</p>
                <p>Well… these are some of the exact questions that were running through founder<strong> Elvio De Andrade&rsquo;s</strong> mind back in 2020. Turns out, the answers were waiting for him right here on the island.</p>

                {/* Inline photo break — visual cadence */}
                <div className="story-inline-photo">
                  <img src="https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=1200&h=600&fit=crop" alt="A cliff view of Madeira" />
                  <span className="story-inline-photo-caption">Looking south from Cabo Girão — 580 m above the Atlantic.</span>
                </div>

                <p>Before creating Madeira Outdoors, Elvio spent years working in marketing while living away from Madeira. But despite the experiences, the travel and the opportunities, something always felt missing: freedom, purpose, and a genuine connection with people and nature.</p>
                <p>Then came the global pause of Covid… and like many others, Elvio started questioning everything.</p>
                <p className="story-belief">That period became the turning point.</p>
                <p>With a bit more maturity, life experience, and finally the courage to take a leap into the unknown, he decided to leave everything behind and return to Madeira. Not simply to come home, but to rediscover the island through a completely different perspective.</p>

                <div className="story-highlight">
                  <h3>And that&rsquo;s how &ldquo;Live in Madeira&rdquo; was born in 2021.</h3>
                </div>

                <p>At first, it was not a company, nor even a business idea. It was simply a creative and personal project built around documenting the island through storytelling, photography, hikes, culture and everyday life.</p>
                <p>Back then, Madeira was still mostly showcased through the lens of Funchal, traditional folklore, or postcard viewpoints. But there was another side of the island that deserved to be seen and preserved.</p>
                <p>Coming from <strong>Santana</strong>, often called the cradle of Northern Madeira, Elvio grew up deeply connected to the island&rsquo;s rural culture, mountain traditions, and way of life shaped by nature, resilience and community.</p>

                {/* Side-by-side mini gallery */}
                <div className="story-mini-gallery">
                  <img src="https://images.unsplash.com/photo-1542601906990-b4d3fb778b09?w=600&h=750&fit=crop" alt="Mossy laurel branches" />
                  <img src="https://images.unsplash.com/photo-1542601906990-b4d3fb778b09?w=600&h=750&fit=crop" alt="Pico Ruivo trail" />
                  <img src="https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=600&h=750&fit=crop" alt="Cabo Girão cliffs" />
                </div>

                <p>So instead of following the classic tourism narrative, Live in Madeira started telling different stories. Old paths connecting villages, levadas carrying generations of history, local expressions, hidden corners of the island, conversations with locals, sunrise hikes above the clouds, and the everyday rhythm of Madeiran life.</p>
                <p>The first people joining these adventures were not clients at all. Just friends wanting to experience the island differently.</p>
                <p>At the time, Elvio was doing everything himself. Scouting routes, taking photos, creating content, organizing experiences and exploring every corner of the island he could find.</p>
                <p className="story-belief">And surprisingly, people connected with it almost immediately. Not because it was trying to go viral, but because it felt honest.</p>
                <p>As the project slowly grew, more people naturally became part of the adventure. Friends turned into collaborators, and eventually a small team formed around the same shared values. Big shoutout to <strong>Rubens, Chris, Maísa and Stéphanie</strong> for becoming part of this journey along the way.</p>
                <p>One of the most important encounters happened high above the island, on the trails of Pico do Arieiro, where Elvio met Chris, another passionate adventurer who shared the same love for road trips, nature, photography and meaningful travel experiences. Together, they later created <strong>Cosy Campers</strong> and continued shaping the vision that would eventually become Madeira Outdoors.</p>
                <p className="story-signature">Today, Madeira Outdoors remains a human-sized project built around authenticity, connection, creativity and deep respect for the island we call home.</p>
              </div>
            </div>

            <div className="story-image-side">
              <img
                src="https://images.unsplash.com/photo-1551632811-561732d1e306?w=800&h=1200&fit=crop"
                alt="Madeira Levadas"
                className="story-img-main"
              />
              <div className="story-image-caption">
                <p>Walking Madeira&rsquo;s levadas at dawn</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* More Than Tours */}
      <section className="story-block-section" data-testid="about-more-than-tours">
        <div className="container">
          <div className="story-block">
            <span className="story-block-label">Our Philosophy</span>
            <h2 className="heading-2">More Than Tours, The Idea Behind Madeira Outdoors</h2>
            <p>Madeira Outdoors was born from a pretty simple idea.</p>
            <p>We wanted to create the kind of experiences that we personally believe travel should be about. Human, immersive, meaningful, and deeply connected to the place you are exploring.</p>
            <p className="story-block-pull">Not rushed tourism.<br />Not overcrowded buses.<br />Not experiences designed only for social media.</p>
            <p>In a world where travel is becoming faster, more commercial, and increasingly disconnected from local reality, authentic experiences are becoming harder to find, especially on organized trips.</p>
            <p>From the very beginning, Madeira Outdoors was never meant to become another classic tour company running the same routes every day for large groups of tourists.</p>
            <p>That has simply never been our vision.</p>
            <p>Our experiences are built around the idea that Madeira should not only be visited, it should be lived.</p>
            <p>Through its mountains, villages, hidden trails, stories and people.</p>
            <p>Long before Madeira Outdoors officially existed, founder Elvio spent years exploring the island through his &ldquo;Live in Madeira&rdquo; project, documenting remote places, local traditions, levadas, cultural stories and the lesser-known side of Madeira that many travelers never truly experience.</p>
            <p>That philosophy naturally became the foundation of Madeira Outdoors.</p>
            <p>Every route, experience and adventure we offer is designed with intention and tested through real exploration before we ever bring guests there.</p>
            <p>We spend countless hours scouting locations, understanding local history, refining itineraries, and finding ways to make each experience feel natural rather than staged.</p>
            <p className="story-block-pull">We often say that our trips are made for people who normally do not enjoy group tours. Because somewhere along the journey, it stops feeling like a tour. Instead, it starts feeling like discovering Madeira with a group of like-minded people sharing moments, stories, meals, laughs and experiences together.</p>
            <p>That human connection is one of the most important parts of what we do.</p>
          </div>
        </div>
      </section>

      {/* Full-width photo break */}
      <section className="about-photo-break">
        <img src="https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=1920&h=900&fit=crop" alt="Madeira coast at dusk" />
        <div className="about-photo-break-overlay">
          <blockquote>
            &ldquo;Madeira should not only be visited — it should be lived.&rdquo;
            <cite>— Elvio</cite>
          </blockquote>
        </div>
      </section>

      {/* A Different Way */}
      <section className="story-block-section alt" data-testid="about-different-way">
        <div className="container">
          <div className="story-block">
            <span className="story-block-label">The Vibe</span>
            <h2 className="heading-2">A Different Way to Experience the Island</h2>
            <p>We run the kind of experiences we personally love doing ourselves.</p>
            <p>Long hikes ending with tired legs and a cold beer somewhere local. Sunrise missions that sounded like a good idea at 4am. Conversations with locals that last longer than expected. Random moments that somehow become the best memory of the trip.</p>
            <p className="story-block-pull">That&rsquo;s the stuff we care about.</p>
            <p>Madeira Outdoors was never built around checking highlights off a list or moving people around the island as fast as possible. There are already enough companies doing that.</p>
            <p>For us, the experience starts way before reaching a viewpoint. It starts in the van, in the atmosphere, the music, the stories, the little coffee stop in a village nobody planned for. The silence during sunrise. The laughs halfway through a hard climb when everyone starts questioning whose idea this hike was in the first place.</p>
            <p className="story-block-pull">That&rsquo;s the Madeira Outdoors vibe.</p>
            <p>We genuinely believe the island is best experienced outdoors. Not rushed, not filtered, and definitely not only through a phone screen.</p>
            <p>The mountains, levadas, forests, villages, ocean cliffs, weather changes, local cafés, poncha bars, old farmers on the side of the trail… all of it is part of the experience.</p>
            <p>And sometimes, the unplanned moments become the best part of the whole trip.</p>
            <p>That&rsquo;s why we keep our groups small and flexible. We want people to feel comfortable, present and connected. Not like they&rsquo;re following a flag through a crowd of 30 people taking the exact same photo.</p>
            <p>Our trips are built for curious people. People who enjoy nature, culture, movement, conversations, good energy and meaningful experiences.</p>
            <p>Some travelers come for the hikes and leave talking about the people they met. Others come for the landscapes and leave reconnecting with themselves a little bit.</p>
            <p className="story-belief">That&rsquo;s the beautiful part of it.</p>
            <p>At the end of the day, Madeira Outdoors is less about tourism and more about sharing moments together in one of the most incredible islands on earth. Simple as that.</p>
          </div>
        </div>
      </section>

      {/* Our Approach (Values) */}
      <section className="values-section-minimal">
        <div className="container">
          <div className="values-intro">
            <h2 className="heading-2">What Guides Us</h2>
            <p className="body-large">Four principles we keep in mind, on every trail and in every van ride.</p>
          </div>
          <div className="values-grid-minimal">
            {values.map((value, index) => (
              <div key={index} className="value-card-minimal" data-testid={`value-card-${index}`}>
                <div className="value-icon-minimal">{value.icon}</div>
                <h3 className="value-title-minimal">{value.title}</h3>
                <p className="value-description-minimal">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why People Choose */}
      <section className="story-block-section" data-testid="about-why-people-choose">
        <div className="container">
          <div className="story-block">
            <span className="story-block-label">Why Travel With Us</span>
            <h2 className="heading-2">Why People Choose to Travel With Madeira Outdoors</h2>
            <p className="story-block-pull">Because we care.</p>
            <p>Not in a corporate &ldquo;customer satisfaction&rdquo; way, but in a real human way.</p>
            <p>We care about the people joining us.<br />We care about the island.<br />We care about the atmosphere during our trips.<br />And we care about creating experiences that actually feel genuine.</p>
            <p>When you travel with Madeira Outdoors, you are not just booking a hike or a tour. You are joining people who genuinely love Madeira and want to share it the way locals and adventurers experience it.</p>
            <p>Not everything is scripted.</p>
            <p>Sometimes the weather changes.<br />Sometimes we stop somewhere unexpectedly because the light is incredible.<br />Sometimes a simple local lunch turns into a two-hour conversation.<br />Sometimes the best moment of the trip happens completely by accident.</p>
            <p>And honestly, we like it that way. Because the best adventures are rarely perfect.</p>
            <p>What makes our experiences special is not only the places we visit, but the feeling throughout the journey.</p>
            <p>The shared effort during a climb.<br />The group energy after a long hike.<br />The quiet moments in nature.<br />The laughs in the van.<br />The feeling of disconnecting from routine and reconnecting with something more real.</p>
            <p>We also believe that <strong>small groups change everything</strong>. It allows us to adapt, connect, move naturally, and create a much more personal atmosphere. By the end of many trips, it honestly feels less like a tour and more like a group of friends exploring the island together.</p>
            <p>We take the important things seriously too: safety, professionalism, local knowledge, sustainability, and respect for the island and its communities always come first.</p>
            <p>But we also believe adventures should feel alive.</p>
            <p className="story-block-pull">Not over-organized.<br />Not robotic.<br />Not forced.<br /><strong>Just real.</strong></p>
            <p>So if you are looking for polished luxury tourism where everything is perfectly timed, we might not be your people.</p>
            <p className="story-belief">But if you are searching for meaningful experiences, beautiful landscapes, human connection, good energy, local culture, adventure and memories that stay with you long after the trip ends… then welcome to Madeira Outdoors.</p>
            <p className="story-signature">We&rsquo;ll probably see you somewhere on a mountain trail soon.</p>

            <div className="story-signoff">
              <p className="story-signoff-name">Elvio De Andrade</p>
              <p className="story-signoff-role">Founder of Madeira Outdoors</p>
            </div>
          </div>
        </div>
      </section>

      {/* Final Message */}
      <section className="final-message-section">
        <div className="container">
          <div className="final-message-content">
            <h2 className="heading-2">Ready to walk Madeira with us?</h2>
            <p className="body-large">Whether you&rsquo;re here for a day or a week, we&rsquo;ll help you experience the island in a way that matters.</p>
            <div className="final-message-cta">
              <a href="/custom-trip" className="btn-primary" data-testid="about-cta-plan-trip">Plan Your Trip</a>
              <a href="/contact" className="btn-on-dark" data-testid="about-cta-contact">Get in Touch</a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
